import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { AppTableComponent, SelectedEvent } from '../shared/table/table.component';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { EmptyCellDirective } from '../shared/table/table.directive';
import { NgForOf, NgIf } from '@angular/common';
import { AvatarBlockModule } from '../shared/avatar-block/avatar-block.module';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { TableTypeNames } from '../../../services/column.service';
import { ColumnMap } from '../../../models/entities/column-map';
import { PropertyManagementCompanyService } from '../../../services/property-management-company/property-management-company.service';
import { SidenavContentService } from '../shared/sidenav/sidenav-content/sidenav-content.service';
import { ScrollService } from '../../../services/scroll.service';
import { GrowlerService } from '../../../services/growler.service';
import { switchMap } from 'rxjs/operators';
import { ViolationStatusRollup } from '../../../models/entities/violation-status';
import { AppWidgetGroupComponent, WidgetGroupItem } from '../shared/widget-group/widget-group.component';
import { ViolationStatement } from '../../../models/entities/violation-statement';
import { StatusChangeSidenavComponent, StatusChangeSidenavInput, StatusChangeSidenavOutput } from '../shared/sidenav/status-change-sidenav/status-change-sidenav.component';
import { LegfiDatePipeModule } from '../../../pipes/format/legfi-date.module';
import { ViolationFiltersSidenavComponent, ViolationFiltersSidenavInput, ViolationFiltersSidenavOutput } from './violation-filters-sidenav/violation-filters-sidenav.component';
import { AppStatusChipComponent } from '../shared/status-chip/status-chip.component';
import { PreventClickDirective } from '../shared/directives/prevent-click.directive';
import { EmptyStateModule } from '../shared/empty-state/empty-state.module';


@UntilDestroy()
@Component({
    selector: 'app-violations-list',
    templateUrl: './violations-list.component.html',
    standalone: true,
    imports: [
        AppTableComponent,
        MatSortModule,
        MatTableModule,
        EmptyCellDirective,
        NgForOf,
        AvatarBlockModule,
        PageHeaderComponent,
        AppWidgetGroupComponent,
        LegfiDatePipeModule,
        NgIf,
        RouterLink,
        AppStatusChipComponent,
        PreventClickDirective,
        EmptyStateModule,
    ],
})
export class ViolationsListComponent implements OnInit, AfterViewInit
{
    @ViewChild(AppTableComponent) tableRef: AppTableComponent<ViolationStatement>;
    @ViewChild(MatSort, { static: true }) columnSort: MatSort;

    queryParams: Params;
    statuses: ViolationStatusRollup = null;
    visibleStatuses: WidgetGroupItem[] = null;

    readonly tableType = TableTypeNames.PMC_VIOLATIONS;
    readonly columnMap: ColumnMap[] = [
        {key: 'mobile', title: 'Violation', exportAs: 'violation', isMobile: true},
        {key: 'title', title: 'Title', exportAs: 'title'},
        {key: 'status', title: 'Status', exportAs: 'status'},
        {key: 'unit', title: 'Unit', exportAs: 'unit'},
        {key: 'owner', title: 'Owner', exportAs: 'owner'},
        {key: 'reportedAt', title: 'Reported', exportAs: 'reported'},
        {key: 'resolutionTime', title: 'Resolve By', exportAs: 'resolution'},
    ];

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _sidenav: SidenavContentService,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    ngOnInit() {
        this._scrollService.scrollToTop();
        this.queryParams = this._activatedRoute.snapshot.queryParams;
    }

    ngAfterViewInit() {
        this.setupTable();
    }

    showStatuses() {
        if (this.tableRef && this.statuses) {
            const statuses = this.statuses.counts.filter((value) => value.violationsCount > 0)
                    .map((value) => {
                        return { label: value.title, total: value.violationsCount };
                    });
            this.visibleStatuses = [...statuses];
        }
    }

    openStatusChange(violation: ViolationStatement) {
        const data: StatusChangeSidenavInput = {
            modelType: 'violations',
            model: violation,
            statuses: this.statuses.data.filter((s) => s.organizationId === violation.organizationId),
        };

        this._sidenav.open(StatusChangeSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: StatusChangeSidenavOutput) => {
            if (result?.success) {
                this.tableRef.refresh();
            }
        });
    }

    goToViolationStatement(event: SelectedEvent<ViolationStatement>) {
        return this._router.navigate([event.source.id, 'details'], {
            relativeTo: this._activatedRoute,
            queryParams: this.queryParams,
        });
    }

    openFilters(filters: { [key: string]: any }) {
        const data: ViolationFiltersSidenavInput = {
            filters,
        };

        this._sidenav.open(ViolationFiltersSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: ViolationFiltersSidenavOutput) => {
            if (result?.success) {
                return this._router.navigate([], {
                    relativeTo: this._activatedRoute,
                    queryParams: {
                        ...result.filters,
                    },
                });
            }
        });
    }

    updateSelectedStatus(widget: WidgetGroupItem) {
        if (widget.label !== this.visibleStatuses[0].label) {
            this.tableRef.addFilter({ violationStatus: widget.label });
        } else {
            this.tableRef.removeFilter('violationStatus');
        }
    }

    private setupTable() {
        this.tableRef.connectDataTable$.pipe(
                switchMap(() => this._propertyManagementCompanyService.getPropertyManagementViolationStatuses(this.tableRef.filters)),
                switchMap((statuses) => {
                    this.statuses = statuses;
                    this.showStatuses();

                    return this._propertyManagementCompanyService.getPropertyManagementViolations(
                            this.tableRef.searchValue,
                            this.columnSort.active,
                            this.columnSort.direction,
                            this.tableRef.paginator.pageIndex + 1,
                            this.tableRef.paginator.pageSize,
                            this.tableRef.filters,
                    );
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => this.tableRef.data$.next(res),
            error: () => {
                this.tableRef.loading = false;
                this._growler.oops('Unable to load violations');
            },
        });
    }
}
